import { Button, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useApiEffect, useDebounce, useFGContext } from "nsitools-react";
import * as React from "react";

import { DogBroodRequestTransientOwnerGridDto, EPlatform, PersonApi, PersonDto } from "../../../../api";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { generateAddress } from "../../../../utils";
import { AddPersonPanel } from "./AddPersonPanel";

export interface IPuppyPersonInformationProps {
  newEmail: string;
  puppyIndex: number;
  owners: DogBroodRequestTransientOwnerGridDto[];
  platform: EPlatform;
  peopleLoading: (loading: boolean) => void;
  addOwner: (owner: DogBroodRequestTransientOwnerGridDto) => void;
  debouncedEmailState: any;
  setdebouncedEmailState: any;
}

export const PuppyPersonInformation: React.FunctionComponent<IPuppyPersonInformationProps> = ({
  newEmail,
  puppyIndex,
  platform,
  peopleLoading,
  addOwner,
  owners,
  debouncedEmailState,
  setdebouncedEmailState,
}) => {
  const { t } = useTl();
  const personApi = useApiService(PersonApi);
  const { formik } = useFGContext();

  const [dialogOpened, setDialogOpened] = React.useState(false);

  const debouncedEmail = useDebounce(newEmail, 1200);

  const [data, loading] = useApiEffect(() => personApi.getPersonByEmail(debouncedEmailState), [debouncedEmailState]);
  const [previousLoading, setPreviousLoading] = React.useState(loading);

  const emailFieldName =
    platform === EPlatform.CARE4DOGS
      ? `puppies[${puppyIndex}].puppyOwnerEmail`
      : `dogBroodRequestGetDto.puppyOwnerEmail`;

  React.useEffect(() => {
    setdebouncedEmailState(debouncedEmail);
  }, [debouncedEmail, setdebouncedEmailState]);

  React.useEffect(() => {
    if (previousLoading !== loading) {
      setPreviousLoading(loading);
      peopleLoading(loading);
    }
  }, [loading, peopleLoading, previousLoading]);

  const getPersonInformation = React.useCallback(
    (item: PersonDto) => (
      <>
        <div>{item.computedName}</div>
        <div>{generateAddress(item.number, item.street, item.city, item.postalCode, item.country)}</div>
        <div>{item.phoneNumber !== null || true ? item.phoneNumber : item.mobilePhoneNumber}</div>
      </>
    ),
    []
  );

  React.useEffect(() => {
    if (data && data.length === 0 && !loading && !dialogOpened && !!debouncedEmailState) {
      if (owners.some((e) => e.email === debouncedEmailState)) {
        setDialogOpened(false);
      } else {
        setDialogOpened(true);
      }
    }
  }, [data, debouncedEmailState, dialogOpened, emailFieldName, formik, loading, owners]);

  const closeDialog = React.useCallback(() => {
    formik.setFieldValue(emailFieldName, null);
    setdebouncedEmailState(null);
    setDialogOpened(false);
  }, [emailFieldName, formik, setdebouncedEmailState]);

  const updateNewPersonInformation = React.useCallback(
    async (newPersonEmail) => {
      var person = await personApi.getPersonByEmail(newPersonEmail).then((p) => {
        if (p.length > 0) {
          return p[0];
        }
      });
      if (person) {
        addOwner(
          new DogBroodRequestTransientOwnerGridDto({
            personId: person.personId,
            computedName: person.computedName,
            email: debouncedEmail,
            address: generateAddress(person.number, person.street, person.city, person.postalCode, person.country),
          })
        );
      }
      formik.setFieldValue(emailFieldName, newPersonEmail);
      setdebouncedEmailState(newPersonEmail);
      setDialogOpened(false);
    },
    [addOwner, debouncedEmail, emailFieldName, formik, personApi, setdebouncedEmailState]
  );

  return (
    data && (
      <>
        {data.map((item) => (
          <div style={{ display: "flex" }}>
            <Button
              style={{ marginTop: "1.2rem", marginBottom: "1.5rem" }}
              minimal={false}
              intent={Intent.PRIMARY}
              icon={IconNames.PLUS}
              disabled={owners.some((e) => e.personId === item.personId)}
              text={t(ETLCodes.AddPersonAsPuppyOwner)}
              onClick={() => {
                formik.setFieldTouched(emailFieldName, true);
                addOwner(
                  new DogBroodRequestTransientOwnerGridDto({
                    personId: item.personId,
                    computedName: item.computedName,
                    email: debouncedEmailState,
                    address: generateAddress(item.number, item.street, item.city, item.postalCode, item.country),
                  })
                );
                formik.setFieldValue(emailFieldName, "");
                setdebouncedEmailState("");
                console.log("set field null");
              }}
            />
            <div style={{ display: "block", margin: "1rem" }}>{getPersonInformation(item)}</div>
          </div>
        ))}

        <Dialog
          title={t(ETLCodes.AddPerson)}
          isOpen={dialogOpened}
          onClose={closeDialog}
          canEscapeKeyClose={true}
          canOutsideClickClose={true}
          isCloseButtonShown={true}
          style={{ width: 1000, height: 600, background: Colors.WHITE }}
          className={Classes.DIALOG}
        >
          <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
            <AddPersonPanel personEmail={newEmail} newEmail={updateNewPersonInformation} />
          </div>
        </Dialog>
      </>
    )
  );
};
